import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { FileText, Download, Loader2, ArrowRight, CheckCircle2, ShieldCheck, Sparkles } from 'lucide-react';
import { getLeadSource } from '@/lib/leadSource';
import { buildBridgeLead } from '@/lib/eventsPortal';
import ConsentChecklist from '@/components/site/ConsentChecklist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  clinic_name: '',
  role: '',
  privacy_consent: false,
  email_newsletter_consent: false,
  whatsapp_newsletter_consent: false,
};

// =============================================================================
// COPY DICTIONARY
// =============================================================================
//
// Two variants share the SAME form, validation, API call, success state and
// lead-attribution mechanism. Differences are limited to copy + asset metadata.
//
//   - default → general Prime Vision brochure (legacy behaviour)
//   - fse     → FSE 2.0 guide (Instagram Reel campaign lead magnet)
//
// To add a new brochure asset, just add a new entry here and pass
// `variant="<key>"` (or override individual props from the caller).
//
// The structured `source` string we send to the backend lets the admin still
// see the original lead source while ALSO carrying campaign / asset / utm —
// without any backend schema change.
// =============================================================================

const COPY = {
  it: {
    default: {
      eyebrow: 'Brochure funzionalità · PDF',
      title_a: 'Vuoi capire se fa per te',
      title_b: 'senza parlare con nessuno?',
      lead:
        'Scarica la brochure: in pochi minuti vedi funzioni, flussi e vantaggi concreti per il tuo studio. PDF immediato. Nessuna telefonata obbligatoria.',
      perks: [
        'Cartella clinica + AI vocale',
        'Agenda smart e follow-up automatici',
        'Percorso FSE 2.0 ordinato',
        'Pricing pre-order 2027',
      ],
      pdfList: [
        { tag: 'Brochure completa', name: 'PRIME_VISION_Brochure.pdf' },
        { tag: 'Executive summary', name: 'PRIME_VISION_Executive_Summary.pdf' },
      ],
      formHeading: 'Ricevi subito la brochure Prime Vision',
      submit: 'Scarica la brochure gratuita',
      success: 'Perfetto! Controlla la tua email: i 2 PDF sono in arrivo.',
      successTitle: 'Brochure inviata',
      successDesc:
        'Ti abbiamo inviato i 2 PDF (brochure completa + executive summary) all\'email che ci hai lasciato. Controlla anche lo spam se non li vedi entro 2 minuti.',
    },
    fse: {
      eyebrow: 'Guida FSE 2.0 · PDF',
      title_a: 'FSE senza caos:',
      title_b: 'cosa cambia per il tuo studio oculistico.',
      lead:
        'Scarica la brochure FSE di Prime Vision e scopri come collegare cartella clinica, documenti, percorso FSE 2.0 e lavoro quotidiano in modo più ordinato. PDF immediato.',
      perks: [
        'Capisci cosa cambia operativamente con il FSE.',
        'Vedi come Prime Vision riduce passaggi manuali e dispersione dati.',
        'Hai un documento pratico da condividere con soci o segreteria.',
      ],
      pdfList: [
        { tag: 'Guida FSE 2.0', name: 'prime-vision-fse-2-0.pdf' },
      ],
      formHeading: 'Ricevi subito la guida FSE 2.0',
      submit: 'Scarica la guida FSE gratuita',
      success: 'Perfetto! Apriamo subito la brochure FSE.',
      successTitle: 'Brochure FSE pronta',
      successDesc:
        'Apriamo subito il PDF. Ti abbiamo anche inviato il link via email — se il PDF non si apre, usa il pulsante qui sotto.',
    },
    // shared
    firstName: 'Nome *',
    lastName: 'Cognome *',
    email: 'Email *',
    phone: 'Telefono (opzionale)',
    phoneRequired: 'Telefono / WhatsApp *',
    clinic: 'Nome studio o clinica (opzionale)',
    role: 'Ruolo (opzionale)',
    privacy_a: 'Accetto la ',
    privacy_b: 'privacy policy',
    privacy_c: ' e il trattamento dei miei dati.',
    submitting: 'Invio in corso…',
    error: 'Qualcosa non ha funzionato. Riprova o scrivici a info@primevision.it',
    required: 'Campo obbligatorio',
    invalidEmail: 'Email non valida',
    trust: 'Confidenziale · GDPR · niente spam',
    successOpenCta: 'Apri / scarica il PDF',
    successResend: 'Inviala a un altro indirizzo',
    successDemo: 'Prenota una demo personalizzata',
  },
  en: {
    default: {
      eyebrow: 'Feature brochure · PDF',
      title_a: 'Want to see if it fits',
      title_b: 'without talking to anyone?',
      lead:
        'Download the brochure: in a few minutes you see features, flows and concrete benefits for your practice. Instant PDF. No mandatory phone call.',
      perks: [
        'Medical records + voice AI',
        'Smart agenda and automatic follow-ups',
        'Compliant FSE 2.0 flow',
        'Pre-order 2027 pricing',
      ],
      pdfList: [
        { tag: 'Full brochure', name: 'PRIME_VISION_Brochure.pdf' },
        { tag: 'Executive summary', name: 'PRIME_VISION_Executive_Summary.pdf' },
      ],
      formHeading: 'Get the Prime Vision brochure now',
      submit: 'Download the free brochure',
      success: 'Perfect! Check your email: the 2 PDFs are on their way.',
      successTitle: 'Brochure sent',
      successDesc:
        'We just sent the 2 PDFs (full brochure + executive summary) to the email you provided. Check spam if you don\'t see them within 2 minutes.',
    },
    fse: {
      eyebrow: 'FSE 2.0 guide · PDF',
      title_a: 'FSE without chaos:',
      title_b: 'what changes for your eye-care practice.',
      lead:
        'Download the Prime Vision FSE brochure and discover how to connect medical records, documents, the FSE 2.0 process and daily work in a more ordered way. Instant PDF.',
      perks: [
        'Understand what operationally changes with FSE.',
        'See how Prime Vision reduces manual steps and data scatter.',
        'Get a practical document to share with partners or front desk.',
      ],
      pdfList: [
        { tag: 'FSE 2.0 guide', name: 'prime-vision-fse-2-0.pdf' },
      ],
      formHeading: 'Get the FSE 2.0 guide now',
      submit: 'Download the free FSE guide',
      success: 'Perfect! Opening the FSE brochure now.',
      successTitle: 'FSE brochure ready',
      successDesc:
        'Opening the PDF now. We also emailed you the link — if the PDF doesn\'t open, use the button below.',
    },
    // shared
    firstName: 'First name *',
    lastName: 'Last name *',
    email: 'Email *',
    phone: 'Phone (optional)',
    phoneRequired: 'Phone / WhatsApp *',
    clinic: 'Practice or clinic name (optional)',
    role: 'Role (optional)',
    privacy_a: 'I accept the ',
    privacy_b: 'privacy policy',
    privacy_c: ' and processing of my data.',
    submitting: 'Sending…',
    error: 'Something went wrong. Please retry or write to info@primevision.it',
    required: 'Required field',
    invalidEmail: 'Invalid email',
    trust: 'Confidential · GDPR · no spam',
    successOpenCta: 'Open / download the PDF',
    successResend: 'Send to another address',
    successDemo: 'Book a personalised demo',
  },
};

/**
 * Read UTM params from the current URL. Used by /fse and /brochure standalone
 * landing pages to capture campaign attribution from Reels / ad links.
 */
function readUTM() {
  if (typeof window === 'undefined') return {};
  const u = new URL(window.location.href);
  const out = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((k) => {
    const v = u.searchParams.get(k);
    if (v && v.trim()) out[k] = v.trim().slice(0, 80);
  });
  return out;
}

/**
 * Build the structured `source` string we ship to the backend.
 * Pipe-separated, capped at 240 chars so the existing DB field never overflows.
 */
function buildSource({ leadSource, fallback, campaign, assetId, utm }) {
  const base = leadSource || getLeadSource(fallback);
  const parts = [base];
  if (campaign) parts.push(`campaign=${campaign}`);
  if (assetId) parts.push(`asset=${assetId}`);
  if (utm) {
    Object.entries(utm).forEach(([k, v]) => parts.push(`${k}=${v}`));
  }
  return parts.join(' | ').slice(0, 240);
}

/**
 * BrochureDownload — single source of truth for every PDF brochure download
 * on the Prime Vision site. Reuses the SAME form, validation, API endpoint
 * (`POST /api/brochure-requests`), success/download UX and lead-attribution
 * mechanism. Variants differ only in copy + asset metadata.
 *
 * Props (all optional — defaults preserve the homepage brochure behaviour):
 *   variant          'default' | 'fse'              — selects copy preset
 *   id               anchor id for the <section>    — default 'brochure'
 *   testIdPrefix     prefix for all data-testids    — default 'pv-brochure'
 *   downloadUrl      absolute or relative PDF path  — enables visible "Open PDF" button
 *                                                     and auto-open after submit
 *   assetId          short asset id                 — packed into `source` as `asset=…`
 *   leadSource       overrides getLeadSource()      — e.g. 'fse-brochure'
 *   fallbackSource   getLeadSource fallback         — default 'brochure-section'
 *   campaign         campaign id                    — packed as `campaign=…`
 *   captureUTM       read utm_* from URL & include  — default false
 *   requirePhone     make phone mandatory           — default false
 *   showExtraFields  show clinic_name + role fields — default false
 *   embedded         drop outer <section> wrapper   — for use inside other pages
 *   hideCopyColumn   skip the left copy column      — for /fse where the hero
 *                                                     already carries title +
 *                                                     subhead + bullets; form
 *                                                     stretches full width
 */
export const BrochureDownload = ({
  variant = 'default',
  id = 'brochure',
  testIdPrefix = 'pv-brochure',
  downloadUrl,
  assetId,
  leadSource,
  fallbackSource = 'brochure-section',
  campaign,
  captureUTM = false,
  requirePhone = false,
  showExtraFields = false,
  embedded = false,
  hideCopyColumn = false,
} = {}) => {
  const { locale } = useLanguage();
  const dict = COPY[locale] || COPY.it;
  const variantCopy = dict[variant] || dict.default;
  const c = { ...dict, ...variantCopy };
  // back-compat: keep success toast text under the same key as before
  c.successToast = variantCopy.success;

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const utm = useMemo(() => (captureUTM ? readUTM() : {}), [captureUTM]);

  const onChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = c.required;
    if (!form.last_name.trim()) e.last_name = c.required;
    if (!form.email.trim()) e.email = c.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = c.invalidEmail;
    if (requirePhone) {
      if (!form.phone.trim() || form.phone.trim().length < 4) e.phone = c.required;
    }
    if (!form.privacy_consent) e.privacy_consent = c.required;
    if (!form.email_newsletter_consent) e.email_newsletter_consent = c.required;
    if (!form.whatsapp_newsletter_consent) e.whatsapp_newsletter_consent = c.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const triggerPdfDownload = () => {
    if (!downloadUrl) return;
    try {
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      /* popup blocked — the visible success button is the user-facing fallback */
    }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error(c.error);
      return;
    }
    setSubmitting(true);
    try {
      // Backend requires phone with min_length=4 — fall back to "n.d." if user didn't leave it.
      const phone = form.phone.trim() ? form.phone.trim() : 'n.d.';
      const source = buildSource({ leadSource, fallback: fallbackSource, campaign, assetId, utm });
      const origin = variant === 'fse' ? 'fse' : 'brochure';
      await axios.post(`${API}/bridge/${origin}`, buildBridgeLead({ ...form, phone }, origin, {
        clinic_name: showExtraFields && form.clinic_name.trim() ? form.clinic_name.trim() : undefined,
        role: showExtraFields && form.role.trim() ? form.role.trim() : undefined,
        locale,
        source_detail: source,
      }));
      setDone(true);
      // Auto-open the PDF for download/preview (subject to popup blockers — the
      // visible "Open PDF" button in the success state is the guaranteed fallback)
      if (downloadUrl) setTimeout(triggerPdfDownload, 300);
      toast.dismiss();
      toast.success(c.successToast);
    } catch (err) {
      console.error(err);
      toast.error(c.error);
    } finally {
      setSubmitting(false);
    }
  };

  const wrapperClass = embedded
    ? 'relative bg-white'
    : 'relative bg-white py-20 md:py-28 border-t border-slate-100';

  const Wrapper = embedded ? 'div' : 'section';

  return (
    <Wrapper
      id={embedded ? undefined : id}
      data-testid={`${testIdPrefix}-section`}
      className={wrapperClass}
    >
      <div className={embedded ? '' : 'pv-container'}>
        <div className={hideCopyColumn ? '' : 'grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 items-start'}>
          {/* LEFT: copy + PDF mock — skipped when hideCopyColumn=true */}
          {!hideCopyColumn && (
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-pv-orange" />
              <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {c.eyebrow}
              </span>
            </div>
            <h2 className="mt-5 font-heading font-bold tracking-normal text-[32px] sm:text-[40px] md:text-[48px] leading-[1.08] text-pv-ink">
              {c.title_a} <br />
              <span className="text-pv-teal">{c.title_b}</span>
            </h2>
            <p className="mt-6 text-slate-600 text-[15.5px] md:text-[17px] leading-[1.65] max-w-md">
              {c.lead}
            </p>

            {/* Perks list */}
            <ul className="mt-7 space-y-2.5">
              {c.perks.map((p, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[14px] text-slate-700 font-medium">
                  <CheckCircle2 className="w-4 h-4 text-pv-orange shrink-0 mt-0.5" />
                  {p}
                </li>
              ))}
            </ul>

            {/* PDF mock list */}
            <div className="mt-9 space-y-3">
              {c.pdfList.map((pdf, idx) => (
                <div key={idx} className="relative inline-flex items-center gap-4 bg-white rounded-xl border border-slate-200 p-4 max-w-sm w-full">
                  <div className="shrink-0 w-11 h-13 rounded-md bg-pv-peach border border-pv-orange/30 flex items-center justify-center relative">
                    <FileText className="w-5 h-5 text-pv-orange" />
                    <span className="absolute -top-1.5 -right-1.5 text-[8.5px] font-mono font-bold tracking-wider bg-pv-orange text-white px-1.5 py-0.5 rounded-md">PDF</span>
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-pv-orangeDeep font-bold">
                      {pdf.tag}
                    </div>
                    <div className="mt-1 text-[12.5px] text-pv-ink font-bold truncate">{pdf.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* RIGHT: form OR success state */}
          <div className={hideCopyColumn ? 'w-full max-w-2xl mx-auto lg:mx-0' : 'lg:col-span-7'}>
            {!done ? (
              <form
                onSubmit={submit}
                className="relative bg-white rounded-2xl p-6 md:p-8 border border-slate-200 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.18)]"
                noValidate
                data-testid={`${testIdPrefix}-form`}
              >
                {c.formHeading && (
                  <h3 className="font-heading font-bold text-[18px] md:text-[20px] text-pv-ink leading-tight mb-5">
                    {c.formHeading}
                  </h3>
                )}
                <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  <Field
                    id={`${testIdPrefix}-first-name-input`}
                    label={c.firstName}
                    testId={`${testIdPrefix}-first-name`}
                    value={form.first_name}
                    onChange={onChange('first_name')}
                    error={errors.first_name}
                    autoComplete="given-name"
                  />
                  <Field
                    id={`${testIdPrefix}-last-name-input`}
                    label={c.lastName}
                    testId={`${testIdPrefix}-last-name`}
                    value={form.last_name}
                    onChange={onChange('last_name')}
                    error={errors.last_name}
                    autoComplete="family-name"
                  />
                  <Field
                    id={`${testIdPrefix}-email-input`}
                    label={c.email}
                    type="email"
                    testId={`${testIdPrefix}-email`}
                    value={form.email}
                    onChange={onChange('email')}
                    error={errors.email}
                    autoComplete="email"
                  />
                  <Field
                    id={`${testIdPrefix}-phone-input`}
                    label={requirePhone ? c.phoneRequired : c.phone}
                    type="tel"
                    testId={`${testIdPrefix}-phone`}
                    value={form.phone}
                    onChange={onChange('phone')}
                    error={errors.phone}
                    autoComplete="tel"
                  />

                  {showExtraFields && (
                    <>
                      <Field
                        id={`${testIdPrefix}-clinic-input`}
                        label={c.clinic}
                        testId={`${testIdPrefix}-clinic`}
                        value={form.clinic_name}
                        onChange={onChange('clinic_name')}
                        autoComplete="organization"
                      />
                      <Field
                        id={`${testIdPrefix}-role-input`}
                        label={c.role}
                        testId={`${testIdPrefix}-role`}
                        value={form.role}
                        onChange={onChange('role')}
                        autoComplete="organization-title"
                      />
                    </>
                  )}

                  <div className="sm:col-span-2 mt-1">
                    <ConsentChecklist
                      form={form}
                      onChange={onChange}
                      errors={errors}
                      testIdPrefix={testIdPrefix}
                      accent="orange"
                    />
                  </div>
                </div>

                <div className="relative mt-7 flex flex-col sm:flex-row items-center gap-4">
                  <button
                    type="submit"
                    data-testid={`${testIdPrefix}-submit`}
                    disabled={submitting}
                    className="pv-btn-primary group w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {c.submitting}
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        {c.submit}
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>

                  <div className="flex items-center gap-2 text-[12.5px] text-slate-500 font-medium">
                    <ShieldCheck className="w-4 h-4 text-pv-teal" />
                    {c.trust}
                  </div>
                </div>
              </form>
            ) : (
              <div
                data-testid={`${testIdPrefix}-success`}
                className="relative bg-white rounded-2xl p-7 md:p-10 border border-emerald-200 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.18)]"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-emerald-100 border border-emerald-200 mb-5">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-emerald-600">
                  {locale === 'en' ? 'delivered · check email' : 'inviata · controlla email'}
                </span>
                <h3 className="mt-3 font-heading font-bold text-[24px] md:text-[30px] text-pv-ink leading-tight tracking-normal">
                  {c.successTitle}
                </h3>
                <p className="mt-4 text-slate-600 text-[15px] leading-relaxed max-w-md">
                  {c.successDesc}
                </p>

                {/* Visible "Open PDF" button — only when a downloadUrl was supplied */}
                {downloadUrl && (
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={assetId || true}
                    data-testid={`${testIdPrefix}-success-download`}
                    className="mt-6 pv-btn-primary w-full sm:w-auto justify-center"
                  >
                    <Download className="w-4 h-4" />
                    {c.successOpenCta}
                  </a>
                )}

                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <a
                    href="/bookacall"
                    className="pv-btn-ghost !text-[13px]"
                    data-testid={`${testIdPrefix}-success-demo`}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {c.successDemo}
                  </a>
                  <button
                    onClick={() => { setDone(false); setForm(initial); }}
                    className="text-[13px] font-semibold text-slate-500 hover:text-pv-teal transition-colors px-2 py-2"
                    data-testid={`${testIdPrefix}-reset`}
                  >
                    {c.successResend}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

const Field = ({ id, label, type = 'text', testId, value, onChange, error, autoComplete }) => {
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2 font-bold">
        {label}
      </label>
      <input
        id={id}
        type={type}
        data-testid={testId}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        className={`pv-input ${error ? 'border-red-400' : ''}`}
      />
      {error && <p className="mt-1.5 text-[11.5px] text-red-500 font-semibold">{error}</p>}
    </div>
  );
};

export default BrochureDownload;
